'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import type { EngagementSummary } from '@/lib/api/types';

type EngagementSummaryParam =
  | EngagementSummary
  | {
      reactions: {
        total: number;
        by_type: Record<string, { count: number; last_at: string }>;
      };
      votes: {
        up: { count: number; last_at: string };
        down: { count: number; last_at: string };
        interested?: { count: number; last_at: string };
      };
    };

interface ReactionDialogState {
  isOpen: boolean;
  objectId: string | null;
  objectType: 'item' | 'entity' | 'initiative' | null;
  title?: string;
  details?: React.ReactNode;
  userReactions?: Array<{ id: string; type: string; reactionType: string; createdAt: string }>;
  engagementSummary?: EngagementSummaryParam;
  onReactionChange?: (
    reactionType: string,
    action: 'add' | 'remove',
    userReactions: string[],
    engagementSummary?: any
  ) => void;
}

interface ReactionDialogContextType {
  state: ReactionDialogState;
  openDialog: (
    objectId: string,
    objectType: 'item' | 'entity' | 'initiative',
    title?: string,
    details?: React.ReactNode,
    userReactions?: Array<{ id: string; type: string; reactionType: string; createdAt: string }>,
    engagementSummary?: EngagementSummaryParam,
    onReactionChange?: (
      reactionType: string,
      action: 'add' | 'remove',
      userReactions: string[],
      engagementSummary?: any
    ) => void
  ) => void;
  closeDialog: () => void;
}

const ReactionDialogContext = createContext<ReactionDialogContextType | undefined>(undefined);

export function ReactionDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ReactionDialogState>({
    isOpen: false,
    objectId: null,
    objectType: null,
    title: undefined,
    details: undefined,
    userReactions: undefined,
    engagementSummary: undefined,
    onReactionChange: undefined,
  });

  const openDialog = (
    objectId: string,
    objectType: 'item' | 'entity' | 'initiative',
    title?: string,
    details?: React.ReactNode,
    userReactions?: Array<{ id: string; type: string; reactionType: string; createdAt: string }>,
    engagementSummary?: EngagementSummaryParam,
    onReactionChange?: (
      reactionType: string,
      action: 'add' | 'remove',
      userReactions: string[],
      engagementSummary?: any
    ) => void
  ) => {
    setState({
      isOpen: true,
      objectId,
      objectType,
      title,
      details,
      userReactions,
      engagementSummary,
      onReactionChange,
    });
  };

  const closeDialog = () => {
    setState({
      isOpen: false,
      objectId: null,
      objectType: null,
      title: undefined,
      details: undefined,
      userReactions: undefined,
      engagementSummary: undefined,
      onReactionChange: undefined,
    });
  };

  return (
    <ReactionDialogContext.Provider value={{ state, openDialog, closeDialog }}>
      {children}
    </ReactionDialogContext.Provider>
  );
}

export function useReactionDialog() {
  const context = useContext(ReactionDialogContext);
  if (!context) {
    throw new Error('useReactionDialog must be used within ReactionDialogProvider');
  }
  return context;
}
